import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import {
  DeliveryGroupSummary,
  InvoiceImportOverview,
  InvoiceImportStatus,
  InvoiceImportSummary,
} from 'src/app/models/invoice-import.model';
import { InvoiceImportService } from 'src/app/services/invoice-import.service';
import { validateInvoiceUploadFiles } from 'src/app/services/invoice-import.util';

@Component({
  selector: 'app-invoice-imports',
  templateUrl: './invoice-imports.component.html',
  styleUrls: ['./invoice-imports.component.scss'],
})
export class InvoiceImportsComponent implements OnInit {
  @Input() storeId!: string;

  section: 'invoices' | 'settings' = 'invoices';
  listView: 'invoices' | 'deliveries' = 'invoices';
  selectedImportId: string | null = null;
  selectedGroupKey: string | null = null;
  returnToGroupKey: string | null = null;

  deliveryGroups: DeliveryGroupSummary[] = [];
  deliveryListError = '';
  deliveryTotal = 0;
  deliveryPage = 1;
  deliveryLimit = 20;
  deliveryStatusFilter: 'open' | 'all' = 'open';

  overview: InvoiceImportOverview | null = null;
  overviewError = '';

  items: InvoiceImportSummary[] = [];
  listError = '';
  total = 0;
  page = 1;
  limit = 20;

  statusFilter: InvoiceImportStatus[] = [];
  dateFrom = '';
  dateTo = '';
  searchQ = '';

  unassignedTotal = 0;
  role = '';

  Math = Math;

  readonly allStatuses: InvoiceImportStatus[] = [
    'pending_parse',
    'parsing',
    'parse_failed',
    'needs_store',
    'ready_for_review',
    'partially_applied',
    'applied',
    'duplicate',
    'rejected_sender',
    'archived',
  ];

  constructor(
    private invoiceImportService: InvoiceImportService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.role = localStorage.getItem('role') || '';
    if (!this.storeId) {
      return;
    }
    this.refreshAll();
    this.loadUnassignedBadge();
  }

  refreshAll(): void {
    this.loadOverview();
    if (this.listView === 'deliveries') {
      this.loadDeliveryGroups();
    } else {
      this.loadList();
    }
  }

  loadOverview(): void {
    this.overviewError = '';
    this.invoiceImportService.getOverview(this.storeId).subscribe({
      next: (res: any) => {
        this.overview = res.response?.data || res.data;
      },
      error: (err: any) => {
        this.overviewError = err.error?.response?.message || err.message || 'Error';
      },
    });
  }

  loadList(): void {
    this.listError = '';
    this.spinner.show();
    const status = this.statusFilter.length ? this.statusFilter.join(',') : undefined;
    this.invoiceImportService
      .listImports(this.storeId, {
        page: this.page,
        limit: this.limit,
        sort: '-deliveryDate',
        status,
        dateFrom: this.dateFrom || undefined,
        dateTo: this.dateTo || undefined,
        q: this.searchQ || undefined,
      })
      .subscribe({
        next: (res: any) => {
          const data = res.response?.data || res.data || {};
          this.items = data.items || [];
          this.total = data.total || 0;
          this.spinner.hide();
        },
        error: (err: any) => {
          this.spinner.hide();
          this.listError = err.error?.response?.message || err.message || 'Error';
        },
      });
  }

  loadUnassignedBadge(): void {
    if (this.role === 'receipt_auditor' || this.role === 'clerk') {
      return;
    }
    this.invoiceImportService.listUnassigned(1, 1).subscribe({
      next: (res: any) => {
        const data = res.response?.data || res.data || {};
        this.unassignedTotal = data.total || 0;
      },
      error: () => {},
    });
  }

  onPageChange(p: number): void {
    this.page = p;
    this.loadList();
  }

  toggleStatus(status: InvoiceImportStatus, checked: boolean): void {
    if (checked) {
      if (!this.statusFilter.includes(status)) {
        this.statusFilter = [...this.statusFilter, status];
      }
    } else {
      this.statusFilter = this.statusFilter.filter((s) => s !== status);
    }
    this.page = 1;
    this.loadList();
  }

  applyFilters(): void {
    this.page = 1;
    this.deliveryPage = 1;
    if (this.listView === 'deliveries') {
      this.loadDeliveryGroups();
    } else {
      this.loadList();
    }
  }

  setListView(view: 'invoices' | 'deliveries'): void {
    if (this.listView === view) {
      return;
    }
    this.listView = view;
    this.page = 1;
    this.deliveryPage = 1;
    if (view === 'deliveries') {
      this.loadDeliveryGroups();
    } else {
      this.loadList();
    }
  }

  loadDeliveryGroups(): void {
    this.deliveryListError = '';
    this.spinner.show();
    this.invoiceImportService
      .listDeliveryGroups(this.storeId, {
        page: this.deliveryPage,
        limit: this.deliveryLimit,
        status: this.deliveryStatusFilter,
        dateFrom: this.dateFrom || undefined,
        dateTo: this.dateTo || undefined,
      })
      .subscribe({
        next: (res: any) => {
          const data = res.response?.data || res.data || {};
          this.deliveryGroups = data.items || [];
          this.deliveryTotal = data.total || 0;
          this.spinner.hide();
        },
        error: (err: any) => {
          this.spinner.hide();
          this.deliveryListError = err.error?.response?.message || err.message || 'Error';
        },
      });
  }

  onDeliveryPageChange(p: number): void {
    this.deliveryPage = p;
    this.loadDeliveryGroups();
  }

  applyDeliveryFilters(): void {
    this.deliveryPage = 1;
    this.loadDeliveryGroups();
  }

  setDeliveryStatusFilter(status: 'open' | 'all'): void {
    this.deliveryStatusFilter = status;
    this.deliveryPage = 1;
    this.loadDeliveryGroups();
  }

  openDeliveryGroup(row: DeliveryGroupSummary): void {
    this.selectedGroupKey = row.groupKey;
  }

  onDeliveryGroupClosed(refresh: boolean): void {
    this.selectedGroupKey = null;
    if (refresh) {
      this.loadOverview();
      this.loadDeliveryGroups();
    }
  }

  openImportFromGroup(importId: string): void {
    this.returnToGroupKey = this.selectedGroupKey;
    this.selectedGroupKey = null;
    this.selectedImportId = importId;
  }

  openDetail(item: InvoiceImportSummary): void {
    this.selectedImportId = item._id;
  }

  openDuplicateImport(duplicateOfImportId: string | null | undefined, event: Event): void {
    event.stopPropagation();
    if (!duplicateOfImportId) {
      return;
    }
    this.selectedImportId = duplicateOfImportId;
  }

  onDetailClosed(refresh: boolean): void {
    this.selectedImportId = null;
    if (this.returnToGroupKey) {
      this.selectedGroupKey = this.returnToGroupKey;
      this.returnToGroupKey = null;
      if (refresh) {
        this.loadOverview();
        this.loadDeliveryGroups();
      }
      return;
    }
    if (refresh) {
      this.refreshAll();
    }
  }

  deliveryInvoiceNumbersLabel(row: DeliveryGroupSummary): string {
    const nums = row.invoiceNumbers || [];
    if (!nums.length) {
      return '—';
    }
    if (nums.length <= 3) {
      return nums.join(', ');
    }
    return `${nums.slice(0, 3).join(', ')} +${nums.length - 3}`;
  }

  copyInboundAddress(): void {
    const addr = this.overview?.inboundAddress;
    if (!addr) {
      return;
    }
    navigator.clipboard?.writeText(addr).then(
      () => this.toastr.success(this.translate.instant('INVOICE_IMPORTS.OVERVIEW.COPIED')),
      () => this.toastr.error(this.translate.instant('INVOICE_IMPORTS.OVERVIEW.COPY_FAILED'))
    );
  }

  onUploadInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) {
      return;
    }
    this.uploadFiles(input.files);
    input.value = '';
  }

  uploadFiles(fileList: FileList): void {
    const { valid, errors } = validateInvoiceUploadFiles(fileList);
    if (errors.includes('too_many')) {
      Swal.fire(
        this.translate.instant('INVOICE_IMPORTS.UPLOAD.ERROR_TITLE'),
        this.translate.instant('INVOICE_IMPORTS.UPLOAD.TOO_MANY'),
        'error'
      );
      return;
    }
    if (!valid.length) {
      Swal.fire(
        this.translate.instant('INVOICE_IMPORTS.UPLOAD.ERROR_TITLE'),
        this.translate.instant('INVOICE_IMPORTS.UPLOAD.INVALID'),
        'error'
      );
      return;
    }
    this.spinner.show();
    this.invoiceImportService.uploadPdfs(this.storeId, valid).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        const data = res.response?.data || res.data || {};
        const ids: string[] = data.importIds || [];
        const dups = data.duplicates || [];
        let msg = this.translate.instant('INVOICE_IMPORTS.UPLOAD.SUCCESS', { count: ids.length });
        if (dups.length) {
          msg += ' ' + this.translate.instant('INVOICE_IMPORTS.UPLOAD.DUPLICATES', { count: dups.length });
        }
        this.toastr.success(msg);
        this.refreshAll();
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire(
          this.translate.instant('INVOICE_IMPORTS.UPLOAD.ERROR_TITLE'),
          err.error?.response?.message || err.message,
          'error'
        );
      },
    });
  }

  goUnassigned(): void {
    this.router.navigate(['/invoice-imports/unassigned']);
  }

  statusLabelKey(status: string): string {
    return `INVOICE_IMPORTS.STATUS.${status.toUpperCase()}`;
  }

  isStatusSelected(status: InvoiceImportStatus): boolean {
    return this.statusFilter.includes(status);
  }
}
